import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TextField } from "ui/components/TextField/TextField";
import { CardSelect } from "ui/components/CardSelect";
import Dropdown from "ui/components/Dropdown/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { OfficeAddress, OfficeRow } from "services/api/offices/types";
import { MarketplaceLocation } from "../../../types";
import { LOCATION_MODE_OPTIONS } from "../../../consts";
import { SectionCard } from "../primitives";

type LocationMode = "office" | "manual";

interface Props {
  values: MarketplaceLocation;
  onChange: (patch: Partial<MarketplaceLocation>) => void;
  officeId: string;
  onOfficeChange: (officeId: string, address: OfficeAddress | null) => void;
  /** Poslovnice tenanta; korak ih učita prije rendera pa su ovdje uvijek spremne. */
  offices: OfficeRow[];
  /** Poruka kad korak ne prolazi jer lokacija nije određena. */
  error?: string;
}

// Poslovnica stoji uz lokaciju jer njen odabir prepisuje polja ispod — korisnik ih poslije
// smije mijenjati, pa odabir služi kao brzi unos, a ne kao zaključana vrijednost.
//
// Način se ne sprema zasebno: ručni unos briše poslovnicu, pa je „ima li oglas poslovnicu"
// dovoljno da se pri sljedećem otvaranju pogodi ispravan način.
const LocationSection: React.FC<Props> = ({
  values,
  onChange,
  officeId,
  onOfficeChange,
  offices,
  error,
}) => {
  // Poslovnice su poznate već pri prvom renderu, pa se način odredi odmah — kad se
  // računao nakon dohvata, odabrana kartica bi vidljivo poskočila.
  const [mode, setMode] = useState<LocationMode>(
    officeId || offices.length ? "office" : "manual"
  );
  // Automatski odabir jedine poslovnice smije se dogoditi samo jednom — inače bi
  // korisnikovo brisanje odabira odmah bilo poništeno.
  const hasAutoSelected = useRef(false);

  // Popunjava jedinu poslovnicu tenanta. Vraća `false` ako izbor nije jednoznačan,
  // da pozivatelj zna je li se što dogodilo.
  const selectOnlyOffice = useCallback(() => {
    if (offices.length !== 1) return false;

    const only = offices[0];
    if (!only.id) return false;

    onOfficeChange(only.id, only.primaryAddress ?? null);
    return true;
  }, [offices, onOfficeChange]);

  useEffect(() => {
    if (hasAutoSelected.current) return;

    hasAutoSelected.current = true;

    if (!officeId) selectOnlyOffice();
  }, [officeId, selectOnlyOffice]);

  const officeOptions: DropdownOption[] = useMemo(
    () =>
      offices.map((office) => ({
        value: office.id ?? "",
        text: office.name ?? "",
      })),
    [offices]
  );

  const handleOfficeChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      const selected = offices.find((office) => office.id === item.value);

      hasAutoSelected.current = true;
      onOfficeChange(item.value ?? "", selected?.primaryAddress ?? null);
    },
    [offices, onOfficeChange]
  );

  const handleModeChange = useCallback(
    (next: string) => {
      if (next === "manual") {
        setMode("manual");
        // Ručni unos kreće od prazne forme — adresa poslovnice ovdje više ne vrijedi.
        onOfficeChange("", null);
        return;
      }

      setMode("office");

      // Jedina poslovnica se i ovdje ponudi sama — inače bi način „iz poslovnice"
      // ostao s praznim odabirom iako izbora zapravo nema.
      if (!officeId) selectOnlyOffice();
    },
    [officeId, onOfficeChange, selectOnlyOffice]
  );

  const handleText = (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ [e.target.name]: e.target.value } as Partial<MarketplaceLocation>);

  return (
    <SectionCard
      title="Lokacija"
      subtitle="Lokacija se preuzima iz poslovnice ili se upisuje ručno."
    >
      <div className="mb-4">
        <CardSelect
          options={LOCATION_MODE_OPTIONS}
          value={mode}
          onChange={handleModeChange}
        />
      </div>

      {mode === "office" && (
        <div className="mb-3">
          <Dropdown
            name="locationOfficeId"
            label="Poslovnica"
            placeholder="Odaberite poslovnicu"
            options={officeOptions}
            value={officeId}
            onChange={handleOfficeChange}
            error={error}
            required
            width="full"
          />
        </div>
      )}

      {/* U načinu „iz poslovnice" polja se ne prikazuju — adresa je ona s poslovnice.
          Prelaskom na ručni unos pojave se popunjena zadnjom poslovnicom, pa se dotjeruju. */}
      {mode === "manual" && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div><TextField type="text" name="countyCode" label="Županija / grad" placeholder="npr. Grad Zagreb" value={values.countyCode} onChange={handleText} error={error} required /></div>
            <div><TextField type="text" name="municipalityCode" label="Općina" placeholder="npr. Peščenica - Žitnjak" value={values.municipalityCode} onChange={handleText} /></div>
            <div><TextField type="text" name="settlementCode" label="Naselje" placeholder="npr. Donje Svetice" value={values.settlementCode} onChange={handleText} error={error} required /></div>
          </div>

          <div className="mt-3">
            <TextField type="text" name="manualAddress" label="Adresa (ručno)" placeholder="npr. Ilica 265" value={values.manualAddress} onChange={handleText} error={error} required />
          </div>
        </>
      )}

      <div className="mt-4 flex flex-col gap-2">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="locationDisplay"
            checked={values.display === "exact"}
            onChange={() => onChange({ display: "exact" })}
          />
          <span className="text-sm">Prikaži točnu lokaciju na karti</span>
        </label>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="locationDisplay"
            checked={values.display === "approximate"}
            onChange={() => onChange({ display: "approximate" })}
          />
          <span className="text-sm">Prikaži približnu lokaciju na karti</span>
        </label>
      </div>
    </SectionCard>
  );
};

export default memo(LocationSection);
